import {
	DateObject,
	type QuestionnaireAnswers,
	type QuestionnaireContent,
	type RequirementIdentifier,
	useIntl,
	type UserUKQuizUserProvidedValues,
	useTimeToText
} from '@capsicum/common';
import { useUser } from '@capsicum/socios-core';
import { useEffect } from 'react';

import { useSubmitRequirementStateByIdentifierQuery } from '../data-access';

type UseUkQuizParams = {
	openBlockedUntilModal: () => void;
	openIntroductoryModal: () => void;
	quizContent: null | QuestionnaireContent;
	showSuccessModal: (onClose: () => void) => void;
};

export const useUkQuiz = ({
	openBlockedUntilModal,
	openIntroductoryModal,
	quizContent,
	showSuccessModal
}: UseUkQuizParams) => {
	const { refetchUser } = useUser();
	const {
		data: submissionResponse,
		isLoading: isUkQuizSubmissionLoading,
		mutate
	} = useSubmitRequirementStateByIdentifierQuery({ shouldNotRefetchCredentialsAtom: true });

	const submitUkQuiz = (requirementIdentifier: RequirementIdentifier, answers: QuestionnaireAnswers) => {
		const answersObject = answers.reduce((acc: UserUKQuizUserProvidedValues['answers'], answer) => {
			if (!acc) acc = {};

			acc[answer.questionId] = answer.answerId;

			return acc;
		}, {});

		mutate({
			requirementData: { answers: answersObject },
			requirementIdentifier: requirementIdentifier
		});
	};

	useEffect(() => {
		if (!submissionResponse) return;

		const isSuccess = (submissionResponse?.data?.userProvidedValues as UserUKQuizUserProvidedValues).successful;

		if (isSuccess) {
			showSuccessModal(refetchUser);
		} else {
			refetchUser();
		}
	}, [submissionResponse]);

	useEffect(() => {
		if (quizContent?.length) {
			openIntroductoryModal();
		} else {
			openBlockedUntilModal();
		}
	}, [quizContent]);

	return {
		isUkQuizSubmissionLoading,
		submissionResponse,
		submitUkQuiz
	};
};

export const useUkQuizBlockedMessage = (blockedUntil: string, attempts: null | number) => {
	const { $t } = useIntl();
	const { formatTime } = useTimeToText();

	const timeDifference = new DateObject(blockedUntil).valueOf() - new DateObject().valueOf();
	const formattedTime = formatTime(timeDifference);

	const message = $t('jurisdictionRequirements.ukQuiz.tryAgainModalText', {
		time: formattedTime
	});

	const attemptsMessage = attempts
		? $t(`jurisdictionRequirements.ukQuiz.${attempts > 1 ? 'attemptsText' : 'attemptText'}`, { attempts })
		: null;

	return {
		attemptsMessage,
		message
	};
};
